// src/components/popups/RewardCodeAwarenessPopup.js
import React, { useState } from 'react';
import './RewardCodeAwarenessPopup.css';
import api from '../../api';
import { useTranslation } from 'react-i18next';

import barterKingIcon from '../../assets/barterking-icon.png';
import merchantStarIcon from '../../assets/merchantstar-icon.png';
import creatorFlameIcon from '../../assets/creatorflame-icon.png';
import guardianIcon from '../../assets/guardian-icon.png';

function RewardCodeAwarenessPopup({
  open,
  rewardCount = 0,
  onPassed,
  onRedeemed,
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const showError = (msg) => {
    setSuccessMsg('');
    setErrorMsg(msg);

    window.clearTimeout(showError._t);
    showError._t = window.setTimeout(() => setErrorMsg(''), 3000);
  };

  const showSuccess = (msg) => {
    setErrorMsg('');
    setSuccessMsg(msg);

    window.clearTimeout(showSuccess._t);
    showSuccess._t = window.setTimeout(() => setSuccessMsg(''), 3500);
  };

  if (!open) return null;

  const handleRedeem = async () => {
    if (loading) return;

    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      const res = await api.post('/plans/redeem-auto');

      showSuccess(
        res?.data?.message || t('reward_popup.success_activated')
      );

      setTimeout(() => {
        if (onRedeemed) {
          onRedeemed(res?.data || null);
        }
      }, 800);
    } catch (err) {
      console.error('❌ Failed to auto redeem reward code:', err);

      const msg =
        err?.response?.data?.message ||
        t('reward_popup.error_activate');

      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePass = () => {
    if (loading) return;
    if (onPassed) onPassed();
  };

  return (
    <div className="reward-awareness-popup-backdrop">
      <div
        className="reward-awareness-popup-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {errorMsg && (
          <div className="reward-awareness-popup-banner error">
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div className="reward-awareness-popup-banner success">
            {successMsg}
          </div>
        )}

        <div className="reward-awareness-popup-bg-shape reward-awareness-popup-bg-shape--top" />
        <div className="reward-awareness-popup-bg-shape reward-awareness-popup-bg-shape--bottom" />

        <div className="reward-awareness-popup-inner">
          <div className="reward-awareness-popup-top">
            <div className="reward-awareness-popup-badges-row">
              <div className="reward-awareness-popup-badge-icon-wrap">
                <img
                  src={barterKingIcon}
                  alt={t('reward_popup.badges.barter_king')}
                  className="reward-awareness-popup-badge-icon"
                />
              </div>

              <div className="reward-awareness-popup-badge-icon-wrap">
                <img
                  src={merchantStarIcon}
                  alt={t('reward_popup.badges.merchant_star')}
                  className="reward-awareness-popup-badge-icon"
                />
              </div>

              <div className="reward-awareness-popup-badge-icon-wrap">
                <img
                  src={creatorFlameIcon}
                  alt={t('reward_popup.badges.creator_flame')}
                  className="reward-awareness-popup-badge-icon"
                />
              </div>

              <div className="reward-awareness-popup-badge-icon-wrap">
                <img
                  src={guardianIcon}
                  alt={t('reward_popup.badges.pi_guardian')}
                  className="reward-awareness-popup-badge-icon"
                />
              </div>
            </div>

            <h2 className="reward-awareness-popup-title">
              {t('reward_popup.title')}
            </h2>

            <p className="reward-awareness-popup-subtitle">
              {t('reward_popup.subtitle')}
            </p>
          </div>

          <div className="reward-awareness-popup-middle">
            <div className="reward-awareness-popup-features-card">
              <div className="reward-awareness-popup-features-grid">
                <div className="reward-awareness-popup-feature-item">
                  <span className="reward-awareness-popup-feature-check">✓</span>
                  <span>{t('reward_popup.features.content_boost')}</span>
                </div>

                <div className="reward-awareness-popup-feature-item">
                  <span className="reward-awareness-popup-feature-check">✓</span>
                  <span>{t('reward_popup.features.feed_spotlight')}</span>
                </div>

                <div className="reward-awareness-popup-feature-item">
                  <span className="reward-awareness-popup-feature-check">✓</span>
                  <span>{t('reward_popup.features.creator_analytics')}</span>
                </div>

                <div className="reward-awareness-popup-feature-item">
                  <span className="reward-awareness-popup-feature-check">✓</span>
                  <span>{t('reward_popup.features.edit_post')}</span>
                </div>

                <div className="reward-awareness-popup-feature-item">
                  <span className="reward-awareness-popup-feature-check">✓</span>
                  <span>{t('reward_popup.features.escrow_protection')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="reward-awareness-popup-bottom">
            <button
              type="button"
              className="reward-awareness-popup-primary-btn"
              onClick={handleRedeem}
              disabled={loading}
            >
              {loading
                ? t('reward_popup.activating')
                : t('reward_popup.activate_button')}
            </button>

            <button
              type="button"
              className="reward-awareness-popup-pass-btn"
              onClick={handlePass}
              disabled={loading}
            >
              {t('reward_popup.pass_button')}
            </button>

            <div className="reward-awareness-popup-footnote">
              {rewardCount > 0
                ? t('reward_popup.footnote_available', { count: rewardCount })
                : t('reward_popup.footnote_auto_use')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RewardCodeAwarenessPopup;
